import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Target, Calendar, Trash2, Check } from "lucide-react";
import { CreateGoalDialog } from "./components/CreateGoalDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/lib/toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Goal } from "@/types/database";

export default function Goals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadGoals = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id, permissions')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
        const permissions = collaboratorData.permissions as any;
        if (!permissions?.reports) {
          setGoals([]);
          setIsLoading(false);
          return;
        }
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        setGoals([]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error loading goals:", error);
        toast.error("Erro ao carregar metas");
        return;
      }

      // Transformar os dados para o tipo Goal correto
      const transformedGoals: Goal[] = (data || []).map(goal => ({
        ...goal,
        goal_type: goal.goal_type as 'postagens' | 'faturamento' | 'outros',
        visible_to: Array.isArray(goal.visible_to) ? goal.visible_to.map(id => String(id)) : []
      }));

      // Filtrar metas visíveis baseado no campo visible_to
      const visibleGoals = transformedGoals.filter(goal => {
        // Se o usuário é o criador da meta, sempre pode ver
        if (goal.user_id === user.id) return true;
        
        // Se tem campo visible_to, verificar se o usuário está na lista
        if (goal.visible_to && Array.isArray(goal.visible_to)) {
          return goal.visible_to.includes(user.id);
        }
        
        // Se é colaborador com permissão de reports, pode ver todas as metas da empresa
        return collaboratorData ? true : false;
      });

      setGoals(visibleGoals);
    } catch (error) {
      console.error("Error loading goals:", error);
      toast.error("Erro ao carregar metas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .update({ completed: true })
        .eq('id', goalId);

      if (error) {
        console.error("Error completing goal:", error);
        toast.error("Erro ao completar meta");
        return;
      }

      toast.success("Meta marcada como concluída!");
      loadGoals();
    } catch (error) {
      console.error("Error completing goal:", error);
      toast.error("Erro ao completar meta");
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (error) {
        console.error("Error deleting goal:", error);
        toast.error("Erro ao excluir meta");
        return;
      }

      toast.success("Meta excluída com sucesso!");
      loadGoals();
    } catch (error) {
      console.error("Error deleting goal:", error);
      toast.error("Erro ao excluir meta");
    }
  };

  const getProgressPercentage = (goal: Goal) => {
    if (!goal.target_value) return 0;
    return Math.min((goal.current_value / goal.target_value) * 100, 100);
  };

  const getGoalTypeColor = (type: string) => {
    switch (type) {
      case 'postagens':
        return 'bg-blue-100 text-blue-800';
      case 'faturamento':
        return 'bg-green-100 text-green-800';
      case 'outros':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    loadGoals();
  }, [user]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-muted-foreground">Carregando metas...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Metas</h1>
          <p className="text-muted-foreground">
            Gerencie suas metas e acompanhe o progresso
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Meta
        </Button>
      </div>

      {goals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma meta criada</h3>
            <p className="text-muted-foreground mb-4">
              Comece criando sua primeira meta para acompanhar o progresso.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Meta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {goals.map((goal) => (
            <Card key={goal.id} className={goal.completed ? "opacity-75" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{goal.title}</CardTitle>
                    <Badge className={getGoalTypeColor(goal.goal_type)}>
                      {goal.goal_type}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    {!goal.completed && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCompleteGoal(goal.id)}
                        title="Marcar como concluída"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteGoal(goal.id)}
                      title="Excluir meta"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Cliente: {goal.client_name}
                  </p>
                  <p className="text-sm">{goal.description}</p>
                </div>

                {goal.target_value && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progresso</span>
                      <span>{goal.current_value} / {goal.target_value}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          goal.completed ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${getProgressPercentage(goal)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {Math.round(getProgressPercentage(goal))}% concluído
                    </p>
                  </div>
                )}

                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4 mr-1" />
                  Prazo: {format(new Date(goal.due_date), "dd/MM/yyyy", { locale: ptBR })}
                </div>

                {goal.completed && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Concluída
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateGoalDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onGoalCreated={loadGoals}
      />
    </div>
  );
}
